import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Lock, User } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { api } from "@/lib/api";

const links = [
  { label: "Opportunities", href: "#opportunities", testid: "nav-link-opportunities" },
  { label: "Mission", href: "#mission", testid: "nav-link-mission" },
  { label: "Services", href: "#services", testid: "nav-link-services" },
  { label: "Insights", href: "#insights", testid: "nav-link-insights" },
  { label: "Why Us", href: "#why-us", testid: "nav-link-why-us" },
  { label: "Consult", href: "#consult", testid: "nav-link-consult" },
  { label: "Contact", href: "#enquire", testid: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((res) => setUser(res.data)).catch(() => setUser(null));
  }, []);

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200 bg-[#FFFFFF]/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <button data-testid="navbar-logo" onClick={() => go("#top")} className="flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="PRE-IPO EXPERT" className="h-10 w-10 rounded-full object-contain" />
          <span className="whitespace-nowrap font-display text-lg font-semibold tracking-[0.18em] text-slate-900">
            PRE-IPO <span className="text-[#6D28D9]">EXPERT</span>
          </span>
        </button>

        <nav className="hidden items-center gap-5 px-4 lg:flex xl:gap-7">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={l.testid}
              onClick={() => go(l.href)}
              className="whitespace-nowrap font-mono2 text-[11px] uppercase tracking-[0.22em] text-slate-500 transition-colors duration-300 hover:text-[#6D28D9]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <Link
            data-testid="nav-client-portal-button"
            to={user ? "/client/dashboard" : "/client/login"}
            className="flex items-center gap-2 border border-[#6D28D9]/40 px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-[#6D28D9] transition-colors duration-300 hover:bg-[#6D28D9]/10"
          >
            <User className="h-3 w-3" /> {user ? `Portal · ${(user.name || "Account").split(" ")[0]}` : "Client Portal · Sign In"}
          </Link>
          <Link
            data-testid="admin-leads-button"
            to="/admin"
            className="flex items-center gap-2 border border-slate-200 px-4 py-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500 transition-colors duration-300 hover:border-[#6D28D9]/40 hover:text-[#6D28D9]"
          >
            <Lock className="h-3 w-3" /> Desk
          </Link>
          <button
            data-testid="nav-cta-enquire-button"
            onClick={() => go("#enquire")}
            className="group relative overflow-hidden bg-[#6D28D9] px-6 py-2.5 font-mono2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFFFFF] transition-transform duration-300 hover:scale-[1.03]"
          >
            <span className="relative z-10">Enquire</span>
            <span className="absolute inset-0 -translate-x-full bg-[#5B21B6] transition-transform duration-300 group-hover:translate-x-0" />
          </button>
        </div>

        <button data-testid="mobile-menu-button" className="lg:hidden text-slate-800" onClick={() => setOpen(!open)}>
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-[#FAF9FF]/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-1 px-6 py-4">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.href)}
                className="py-3 text-left font-mono2 text-xs uppercase tracking-[0.22em] text-slate-700"
              >
                {l.label}
              </button>
            ))}
            <Link data-testid="nav-client-portal-button-mobile" to={user ? "/client/dashboard" : "/client/login"} className="py-3 font-mono2 text-xs uppercase tracking-[0.22em] text-[#6D28D9]">
              {user ? "Client Portal" : "Client Portal · Sign In"}
            </Link>
            <Link data-testid="admin-leads-button-mobile" to="/admin" className="py-3 font-mono2 text-xs uppercase tracking-[0.22em] text-[#6D28D9]">
              Admin Desk
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
};
